import Layout from "../components/Layout";
import { useRouter } from "@tanstack/react-router";
import { MapPin, Star } from "lucide-react";
import { motion } from "motion/react";

const reviews = [
  {
    name: "Tony R.",
    date: "October 2024",
    text: "Just moved to new location in Mohawk Center starting in May 2024. Huge mat space. Great instruction. Really focused on developing techniques. They have added, Muay Thai, fitness kickboxing, MMA, and boxing.",
  },
  {
    name: "Jordan K.",
    date: "August 2024",
    text: "Top notch training, people, and overall amazing vibez",
  },
  {
    name: "Marcus B.",
    date: "May 2024",
    text: "Best gym around. Good people. Everyone is super nice and helpful. If you go once, you'll be hooked.",
  },
  {
    name: "Sarah M.",
    date: "September 2024",
    text: "Coach Mike and his team are incredible. My kids have been training here for 6 months and the confidence boost they've experienced is amazing. The atmosphere is welcoming and everyone is so supportive.",
  },
  {
    name: "James T.",
    date: "July 2024",
    text: "Started as a complete beginner and never felt out of place. The instructors break everything down step by step. Best decision I made for my fitness and self-defense skills.",
  },
  {
    name: "Linda C.",
    date: "August 2024",
    text: "My daughter joined the kids program at age 6 and it's been transformative. She's more focused in school, stands up for herself, and loves going to class. Highly recommend for any parent.",
  },
  {
    name: "Derek P.",
    date: "June 2024",
    text: "Zero ego culture here. Everyone from white belts to advanced students helps each other. The Muay Thai and BJJ programs are both excellent. Worth every penny.",
  },
  {
    name: "Amanda W.",
    date: "September 2024",
    text: "Zach and Kevin are phenomenal coaches. I came in with zero experience and now I'm sparring and loving it. The whole team makes you feel like family from day one.",
  },
  {
    name: "Chris N.",
    date: "October 2024",
    text: "My teenage son needed structure and confidence. Six months at Superhero JJ and he's a different kid — more respectful, focused, and proud. The teens program is outstanding.",
  },
  {
    name: "Rebecca L.",
    date: "July 2024",
    text: "The wrestling and BJJ combination here is elite. I've trained at other gyms in Eugene and nothing compares. The facility is clean, the mats are huge, and the instruction is world-class.",
  },
  {
    name: "Miguel S.",
    date: "May 2024",
    text: "Came for the free class, stayed for the community. It's been 8 months now and I look forward to every session. Fitness has improved dramatically and I've made lifelong friends.",
  },
  {
    name: "Heather G.",
    date: "August 2024",
    text: "Sarah Keim's classes are amazing — she makes every student feel seen and capable. The women's classes are empowering and the entire gym has a safe, supportive energy.",
  },
  {
    name: "Tyler F.",
    date: "June 2024",
    text: "I drove from Eugene specifically to train here after hearing about it from a friend. Totally worth it. The MMA program combines BJJ, wrestling, and striking perfectly.",
  },
  {
    name: "Brittany H.",
    date: "September 2024",
    text: "Our whole family trains here — my husband does MMA, I do Muay Thai, and the kids do BJJ. It's become our thing. The community is what makes this place special.",
  },
  {
    name: "Aaron D.",
    date: "October 2024",
    text: "Won a local tournament after just 4 months of training here. The coaches are detail-oriented and push you to get better while keeping it fun. Superhero JJ is the real deal.",
  },
];

const STAR_KEYS = ["s1", "s2", "s3", "s4", "s5"];

function StarRating({ count = 5 }: { count?: number }) {
  return (
    <div
      className="flex items-center gap-0.5"
      aria-label={`${count} out of 5 stars`}
    >
      {STAR_KEYS.slice(0, count).map((k) => (
        <Star key={k} className="w-4 h-4 fill-gold text-gold" />
      ))}
    </div>
  );
}

function GoogleBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-body font-semibold px-2 py-0.5 rounded-full bg-background/10 border border-primary-foreground/20 text-primary-foreground/70 tracking-wide">
      <svg viewBox="0 0 24 24" className="w-3 h-3" aria-hidden="true">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
      Google Review
    </span>
  );
}

function ReviewCard({
  review,
  index,
}: {
  review: (typeof reviews)[0];
  index: number;
}) {
  const initials = review.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
      viewport={{ once: true }}
      className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-4 shadow-sm hover:shadow-elevated transition-shadow duration-300"
      data-ocid={`review-card-${index}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center flex-shrink-0">
            <span className="text-primary-foreground font-display font-bold text-sm">
              {initials}
            </span>
          </div>
          <div>
            <p className="font-display font-semibold text-sm text-foreground leading-tight">
              {review.name}
            </p>
            <p className="text-xs text-muted-foreground font-body">
              {review.date}
            </p>
          </div>
        </div>
        <GoogleBadge />
      </div>

      {/* Stars */}
      <StarRating />

      {/* Review text */}
      <p className="text-sm text-muted-foreground font-body leading-relaxed flex-1">
        "{review.text}"
      </p>
    </motion.div>
  );
}

export default function ReviewsPage() {
  const router = useRouter();

  return (
    <Layout>
      {/* SEO meta */}
      <title>Reviews | Superhero Jiu Jitsu Academy</title>

      {/* ── Hero ── */}
      <section className="relative bg-primary text-primary-foreground overflow-hidden">
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80 pointer-events-none" />
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-10 right-20 w-72 h-72 rounded-full border border-gold/30" />
          <div className="absolute -bottom-10 -left-10 w-56 h-56 rounded-full border border-gold/20" />
        </div>

        <div className="relative container mx-auto px-4 sm:px-6 py-20 md:py-28 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-gold/20 border border-gold/40 text-gold text-xs font-body font-semibold px-4 py-1.5 rounded-full mb-6"
          >
            <MapPin className="w-3.5 h-3.5" />
            Springfield, OR
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display font-black text-4xl sm:text-5xl md:text-6xl leading-tight mb-5"
          >
            What Our Students
            <br />
            <span className="text-gold">Are Saying</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-primary-foreground/80 font-body text-lg max-w-2xl mx-auto mb-10"
          >
            Join a community of students who've transformed their fitness,
            confidence, and skills at Superhero Jiu Jitsu Academy in
            Springfield, OR.
          </motion.p>

          {/* Overall rating badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="inline-flex flex-col items-center gap-3 bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/20 rounded-2xl px-8 py-6"
          >
            <div className="flex items-center gap-3">
              <span className="font-display font-black text-5xl text-gold leading-none">
                5.0
              </span>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-0.5">
                  {STAR_KEYS.map((k) => (
                    <Star key={k} className="w-5 h-5 fill-gold text-gold" />
                  ))}
                </div>
                <span className="text-xs text-primary-foreground/60 font-body">
                  Based on 15+ Google Reviews
                </span>
              </div>
            </div>

            {/* Google attribution */}
            <div className="flex items-center gap-2 border-t border-primary-foreground/15 pt-3 w-full justify-center">
              <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span className="text-xs text-primary-foreground/60 font-body">
                Reviews from Google Maps
              </span>
              <a
                href="https://maps.app.goo.gl/TkkKkNxW6u45nJHx9"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gold underline hover:opacity-80 transition-opacity font-body"
              >
                View on Google
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Reviews Grid ── */}
      <section className="bg-background py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display font-black text-3xl md:text-4xl text-foreground mb-3">
              Real Reviews from Real Members
            </h2>
            <p className="text-muted-foreground font-body max-w-xl mx-auto">
              Every review below is sourced directly from Google Maps —
              unfiltered and authentic.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, i) => (
              <ReviewCard key={review.name} review={review} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="bg-primary text-primary-foreground py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <div className="flex justify-center mb-6">
              <div className="flex items-center gap-0.5">
                {STAR_KEYS.map((k) => (
                  <Star key={k} className="w-6 h-6 fill-gold text-gold" />
                ))}
              </div>
            </div>
            <h2 className="font-display font-black text-3xl md:text-4xl mb-4">
              Ready to Join Our Community?
            </h2>
            <p className="text-primary-foreground/80 font-body text-lg mb-8">
              Experience the Superhero Jiu Jitsu Academy difference for
              yourself. Book your free class today.
            </p>
            <button
              type="button"
              onClick={() => router.navigate({ to: "/contact-us" })}
              className="inline-flex items-center gap-2 bg-gradient-accent text-foreground font-display font-bold text-base px-8 py-4 rounded-xl shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth cursor-pointer"
              data-ocid="reviews-cta"
            >
              Book Your Free Class
            </button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
